import { $iq } from 'strophe.js';
import logger from './logger';
/**
 * XMPP namespace for HTTP File Upload (XEP-0363).
 */
const NS_HTTP_UPLOAD = 'urn:xmpp:http:upload:0';
/**
 * The only request headers the client is allowed to forward to the storage server when
 * performing the PUT, as defined by XEP-0363.
 */
const ALLOWED_HEADERS = ['authorization', 'cookie', 'expires'];
/**
 * How long (ms) to wait for the upload slot IQ response before giving up.
 */
const SLOT_REQUEST_TIMEOUT = 15000;
/**
 * Requests an HTTP upload slot (XEP-0363) from the storage component.
 *
 * @param {IXmppConnection} connection - The XMPP connection used to send the IQ.
 * @param {string} uploadComponent - The JID of the http_file_share component.
 * @param {File} file - The file to be uploaded.
 * @returns {Promise<IUploadSlot>} Resolves with the PUT/GET URLs and headers.
 */
function requestSlot(connection, uploadComponent, file) {
    return new Promise((resolve, reject) => {
        const iq = $iq({
            to: uploadComponent,
            type: 'get'
        }).c('request', {
            'content-type': file.type || 'application/octet-stream',
            filename: file.name,
            size: String(file.size),
            xmlns: NS_HTTP_UPLOAD
        });
        connection.sendIQ(iq.tree(), (result) => {
            const put = result.getElementsByTagNameNS(NS_HTTP_UPLOAD, 'put')[0];
            const get = result.getElementsByTagNameNS(NS_HTTP_UPLOAD, 'get')[0];
            const putUrl = put?.getAttribute('url');
            const getUrl = get?.getAttribute('url');
            if (!putUrl || !getUrl) {
                reject(new Error('Malformed upload slot response'));
                return;
            }
            const headers = {};
            const headerElements = put.getElementsByTagName('header');
            for (let i = 0; i < headerElements.length; i++) {
                const name = headerElements[i].getAttribute('name');
                if (name && ALLOWED_HEADERS.includes(name.toLowerCase())) {
                    headers[name] = headerElements[i].textContent ?? '';
                }
            }
            resolve({
                getUrl,
                headers,
                putUrl
            });
        }, (err) => reject(err ?? new Error('Upload slot request failed')), SLOT_REQUEST_TIMEOUT);
    });
}
/**
 * Uploads the file bytes to the storage server with an HTTP PUT, reporting progress.
 *
 * @param {IUploadSlot} slot - The upload slot obtained from the storage component.
 * @param {File} file - The file to upload.
 * @param {Function} onProgress - Called with a 0-100 percentage as the upload proceeds.
 * @returns {Promise<void>} Resolves once the PUT succeeds.
 */
function putFile(slot, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', slot.putUrl, true);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        for (const [name, value] of Object.entries(slot.headers)) {
            xhr.setRequestHeader(name, value);
        }
        xhr.upload.onprogress = event => {
            if (event.lengthComputable) {
                // Cap at 99% so the UI reaches 100% only once the file is actually shared.
                onProgress(Math.min((event.loaded / event.total) * 100, 99));
            }
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            }
            else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
    });
}
/**
 * Uploads a file out-of-band via XEP-0363 (HTTP File Upload) and returns the URL it can be
 * downloaded from.
 *
 * Custom (Vianova): the file bytes never travel over the XMPP signaling channel. They are
 * PUT to an ephemeral storage component (Prosody mod_http_file_share) and only the resulting
 * GET URL is shared in-band. The stored file disappears when it expires or when the room is
 * destroyed, so nothing is kept permanently.
 *
 * @param {IXmppConnection} connection - The XMPP connection used to request the slot.
 * @param {string} uploadComponent - The JID of the http_file_share component.
 * @param {File} file - The file to upload.
 * @param {Function} onProgress - Called with a 0-100 percentage as the upload proceeds.
 * @returns {Promise<string>} Resolves with the download URL of the uploaded file.
 */
export async function uploadFileViaHttp(connection, uploadComponent, file, onProgress) {
    const slot = await requestSlot(connection, uploadComponent, file);
    logger.debug(`Obtained upload slot for ${file.name}`);
    await putFile(slot, file, onProgress);
    return slot.getUrl;
}
